import { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import { WA_NUMBER } from './SiteChrome'

export type TallyProduct = {
  name: string
  slug: string
  sku: string
  image: string
}

export const TALLY_CATALOG_PAGE_SIZE = 12

type Theme = {
  border: string
  bg: string
  cardBg: string
  primaryText: string
  hoverText: string
  buttonBg: string
  buttonText: string
  ring: string
  placeholderBg: string
}

function productMatches(product: TallyProduct, query: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase('id-ID')
  if (!normalizedQuery) return true

  return `${product.name} ${product.sku}`.toLocaleLowerCase('id-ID').includes(normalizedQuery)
}

export function getTallyCatalogPage(
  products: TallyProduct[],
  query: string,
  requestedPage: number,
  pageSize: number = TALLY_CATALOG_PAGE_SIZE,
) {
  const filteredProducts = products.filter((product) => productMatches(product, query))
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize))
  const currentPage = Math.min(Math.max(1, requestedPage), totalPages)
  const firstIndex = (currentPage - 1) * pageSize

  return {
    filteredProducts,
    totalPages,
    currentPage,
    firstIndex,
    visibleProducts: filteredProducts.slice(firstIndex, firstIndex + pageSize),
  }
}

function ProductLink({
  product,
  children,
  className,
  ariaLabel,
  listName,
}: {
  product: TallyProduct
  children: React.ReactNode
  className?: string
  ariaLabel?: string
  listName: string
}) {
  return (
    <a
      href={`/produk/${product.slug}`}
      aria-label={ariaLabel}
      className={className}
      onClick={() =>
        trackSelectItem(
          { item_id: product.sku, item_name: product.name, item_category: product.sku },
          listName,
        )
      }
    >
      {children}
    </a>
  )
}

function ProductCard({
  product,
  pageCategory,
  listName,
  theme,
}: {
  product: TallyProduct
  pageCategory: string
  listName: string
  theme: Theme
}) {
  const message = `Halo Dropify, saya mau cek ${product.name}, nomor model ${product.sku}. Kebutuhan: ___ lusin. Ukuran/warna: ___. Kota tujuan: ___. Bisa kirim foto stok dan harganya?`

  return (
    <article className={`group flex flex-col overflow-hidden rounded-3xl border ${theme.border} ${theme.cardBg} transition-all hover:-translate-y-1 hover:shadow-xl`}>
      <ProductLink product={product} listName={listName} ariaLabel={`Lihat detail ${product.name}`} className={`relative block overflow-hidden ${theme.bg}`}>
        <img src={product.image} alt={product.name} width="640" height="640" loading="lazy" decoding="async" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      </ProductLink>
      <div className="flex flex-1 flex-col p-5">
        <p className="break-all text-[11px] font-bold uppercase tracking-wide text-slate-500">{product.sku}</p>
        <h3 className="mt-2 text-lg font-black tracking-tight text-slate-900">
          <ProductLink product={product} listName={listName} className={`hover:${theme.hoverText}`}>{product.name}</ProductLink>
        </h3>
        <ProductLink product={product} listName={listName} className={`mt-3 inline-flex items-center gap-2 text-sm font-black ${theme.primaryText}`}>
          Lihat detail produk <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
        </ProductLink>
        <a
          href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          {...whatsAppCtaProps('landing_page', {
              cta_placement: 'product_card',
              lp_category: pageCategory,
              lead_item_name: product.name,
              items: [{ item_id: product.sku, item_name: product.name, item_category: pageCategory }],
            })}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-black text-white transition-colors hover:bg-green-800"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
          Tanya stok
        </a>
      </div>
    </article>
  )
}

export function TallyProductCatalog({
  products,
  pageCategory,
  listName,
  anchorId,
  theme,
  searchPlaceholder,
  emptyMessage,
}: {
  products: TallyProduct[]
  pageCategory: string
  listName: string
  anchorId: string
  theme: Theme
  searchPlaceholder: string
  emptyMessage: string
}) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const { filteredProducts, totalPages, currentPage, firstIndex, visibleProducts } = useMemo(
    () => getTallyCatalogPage(products, query, page),
    [products, query, page],
  )

  const changePage = (nextPage: number) => {
    setPage(nextPage)
    document.getElementById(anchorId)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  const updateQuery = (value: string) => {
    setQuery(value)
    setPage(1)
  }

  return (
    <div id={anchorId} className="scroll-mt-6">
      <div className={`mt-8 flex flex-col gap-4 rounded-2xl border ${theme.border} ${theme.cardBg} p-4 sm:flex-row sm:items-center sm:justify-between`}>
        <label className="relative block w-full sm:max-w-md">
          <span className="sr-only">{searchPlaceholder}</span>
          <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => updateQuery(event.target.value)}
            placeholder={searchPlaceholder}
            className={`w-full rounded-xl border ${theme.border} ${theme.placeholderBg} py-3 pl-11 pr-11 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 ${theme.ring}`}
          />
          {query && (
            <button type="button" onClick={() => updateQuery('')} aria-label="Hapus pencarian" className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800">
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </button>
          )}
        </label>
        <p className="flex-shrink-0 text-sm font-bold text-slate-600" aria-live="polite">
          {query.trim()
            ? `${filteredProducts.length} model ditemukan`
            : `Menampilkan ${firstIndex + 1}–${Math.min(firstIndex + TALLY_CATALOG_PAGE_SIZE, filteredProducts.length)} dari ${filteredProducts.length} model`}
        </p>
      </div>

      {visibleProducts.length > 0 ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {visibleProducts.map((product) => (
            <ProductCard key={product.sku} product={product} pageCategory={pageCategory} listName={listName} theme={theme} />
          ))}
        </div>
      ) : (
        <div className={`mt-6 rounded-3xl border ${theme.border} ${theme.cardBg} px-5 py-14 text-center`}>
          <p className="font-black text-slate-900">{emptyMessage}</p>
          <p className="mt-2 text-sm text-slate-600">Coba masukkan nama atau nomor model.</p>
          <button type="button" onClick={() => updateQuery('')} className={`mt-5 rounded-xl border ${theme.border} px-5 py-2.5 text-sm font-black ${theme.primaryText} hover:bg-slate-50`}>Hapus pencarian</button>
        </div>
      )}

      {totalPages > 1 && (
        <nav aria-label={`Halaman katalog ${pageCategory}`} className="mt-9 flex flex-wrap items-center justify-center gap-2">
          <button type="button" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1} className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
            <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5" /> Sebelumnya
          </button>
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
            <button key={pageNumber} type="button" onClick={() => changePage(pageNumber)} aria-label={`Halaman ${pageNumber}`} aria-current={pageNumber === currentPage ? 'page' : undefined} className={`h-11 min-w-11 rounded-xl border px-3 text-sm font-black ${pageNumber === currentPage ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-50'}`}>
              {pageNumber}
            </button>
          ))}
          <button type="button" onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages} className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
            Berikutnya <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5" />
          </button>
        </nav>
      )}
    </div>
  )
}
